"use client";

import { useState } from "react";
import ImportDataPaket from "@/components/private/ImportDataPaket";
import StepNavigation from "@/components/private/StepNavigation";
import { Card } from "@/components/ui/card";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import ProgressIndicator from "../ProgressIndicator";
import { useUserPackageRegistration } from "@/hooks/useUserPackageRegistration";
import { useQueryClient } from "@tanstack/react-query";
import InfoKonvensiFees from "./InfoKonvensiFees";
import PicPackageInfoList from "./PicPackageInfoList";

type UserShape = {
    participant_id: number;
    role_id: number;
};

type ActivePeriodShape = {
    id: number;
};

type Props = {
    user: UserShape;
    activePeriod: ActivePeriodShape;
};

export default function PicImportPackage({ user, activePeriod }: Props) {
    const queryClient = useQueryClient();
    const participantId = user.participant_id;
    const periodId = activePeriod.id;
    // 🔹 Ambil fungsi simpan paket user
    const { saveOrUpdateRegistration } = useUserPackageRegistration(
        participantId,
        periodId
    );

    // PIC doesn't need to fetch packageTree or registration data.
    // PicImportPackage only renders import UI and navigation.
    const updateStep = async (step: number) => {
        try {
            await axiosClient.put(
                `/participants/${user.participant_id}/registrations/step/${activePeriod.id}`,
                { current_step: step }
            );
            toast.success("Langkah diperbarui.");
        } catch (err) {
            console.error("PIC updateStep error:", err);
            toast.error("Gagal memperbarui langkah.");
        }
    };


    const handleNext = async () => {
        try {
            // 1. Simpan Pendaftaran
            await saveOrUpdateRegistration.mutateAsync({
                period_id: periodId,
                package_id: 1,
                note: "PIC import paket",
            });

            // 2. Update Step ke Backend
            await axiosClient.put(
                `/participants/${user.participant_id}/registrations/step/${activePeriod.id}`,
                { current_step: 2 }
            );

            // 3. PENTING: Paksa React Query refresh data pendaftaran
            // Ini agar StepGuard tahu bahwa sekarang sudah boleh di Step 2
            await queryClient.invalidateQueries({ queryKey: ["registration-check"] });
            await queryClient.invalidateQueries({ queryKey: ["registration-step"] });

            toast.success("Lanjut ke konfirmasi");
        } catch (err) {
            console.error("Error handleNext:", err);
            throw err; // Lempar error agar StepNavigation tidak pindah halaman
        }
    };

    const [participantCount, setParticipantCount] = useState<number>(0);

    return (
        <div className="space-y-6">
            <Card className="relative border-0 shadow-lg bg-white dark:bg-gray-800 mb-6 overflow-hidden rounded-2xl transition-all duration-300">
                <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 left-0 w-40 md:w-48 opacity-40 pointer-events-none z-0" />
                <img src="/ornamen/ukiran-corner.png" alt="Ukiran Bali" className="absolute top-0 right-0 w-40 md:w-48 scale-x-[-1] opacity-40 pointer-events-none z-0" />
                {/* Dot Pattern Layer */}
                <div
                    className="absolute inset-0 opacity-[0.1] dark:opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage: `radial-gradient(currentColor 0.8px, transparent 0.8px)`,
                        backgroundSize: '12px 12px',
                    }}
                />

                {/* Glow Blur Background Central */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-64 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* Content Area */}
                <div className="relative z-10 px-16 py-8 flex flex-col items-center text-center space-y-3">
                    <div className="space-y-1">
                        <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white flex items-center justify-center gap-2 uppercase">
                            {user.role_id === 2 ? "Pilih Paket Kegiatan" : "Import Data Paket"}
                        </h2>

                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed italic max-w-sm mx-auto">
                            {user.role_id === 2
                                ? "Tentukan pengalaman terbaik Anda melalui pilihan paket kegiatan kami."
                                : "Kelola data peserta secara efisien melalui unggahan berkas Excel kolektif."}
                        </p>
                    </div>
                </div>
            </Card>

            <ProgressIndicator currentStep={1} />

            <ImportDataPaket onDataChange={(count) => setParticipantCount(count)} />
            
            {/* Informasi Paket untuk PIC dipindah ke bawah tabel */}
            <PicPackageInfoList periodId={activePeriod.id} />

            <InfoKonvensiFees />

            <StepNavigation
                onNext={handleNext} 
                onBack={() => updateStep(1)}
                nextDisabled={participantCount === 0}
            />
        </div>
    );
}
